import {
	useEffect,
	useMemo,
	useRef,
	useState
} from 'react';

import PropTypes from 'prop-types';

import createPlotlyComponent from 'react-plotly.js/factory';
import Plotly from 'plotly.js-dist-min';

import {
	resolveChartFigure
} from './chartBuilders';

import './ChartRenderer.css';

const Plot =
	createPlotlyComponent(Plotly);

function convertToError(error) {
	if (error instanceof Error) {
		return error;
	}

	return new Error(
		String(error)
	);
}

export function ChartRenderer({
	chart,
	height = 540,
	className = '',
	style,
	config,
	onError
}) {
	const [state, setState] =
		useState({
			status: 'loading',
			figure: null,
			error: null
		});

	const onErrorRef =
		useRef(onError);

	useEffect(() => {
		onErrorRef.current =
			onError;
	}, [onError]);

	useEffect(() => {
		let isActive = true;

		setState({
			status: 'loading',
			figure: null,
			error: null
		});

		/*
		 * No fetch occurs.
		 *
		 * resolveChartFigure reads GeoJSON directly
		 * from geoFile.config.js.
		 */
		Promise.resolve(
			resolveChartFigure(chart)
		)
			.then((figure) => {
				if (!isActive) {
					return;
				}

				setState({
					status: 'ready',
					figure,
					error: null
				});
			})
			.catch((reason) => {
				if (!isActive) {
					return;
				}

				const error =
					convertToError(reason);

				console.error(
					`Chart "${chart?.chart_id}" failed:`,
					error
				);

				setState({
					status: 'error',
					figure: null,
					error
				});

				onErrorRef.current?.(
					error
				);
			});

		return () => {
			isActive = false;
		};
	}, [chart]);

	const plotProperties =
		useMemo(() => {
			if (
				state.status !==
					'ready' ||
				!state.figure
			) {
				return null;
			}

			const figure =
				state.figure;

			return {
				data:
					figure.data || [],

				layout: {
					...figure.layout,

					autosize: true,

					height:
						typeof figure.layout
							?.height ===
							'number'
							? figure.layout
									.height
							: height
				},

				config: {
					responsive: true,

					displaylogo: false,

					displayModeBar:
						!figure.isMap,

					...figure.config,

					...config
				}
			};
		}, [
			state,
			height,
			config
		]);

	const rootClassName = [
		'plotly-chart',
		className
	]
		.filter(Boolean)
		.join(' ');

	const rootStyle = {
		width: '100%',
		height,
		...style
	};

	if (
		state.status === 'loading'
	) {
		return (
			<div
				className={
					`${rootClassName} ` +
					'plotly-chart--loading'
				}
				style={rootStyle}
				role="status"
				aria-live="polite"
			>
				<span className="plotly-chart__loader" />

				<span>
					در حال آماده‌سازی نمودار...
				</span>
			</div>
		);
	}

	if (
		state.status === 'error'
	) {
		return (
			<div
				className={
					`${rootClassName} ` +
					'plotly-chart--error'
				}
				style={rootStyle}
				role="alert"
			>
				<strong>
					نقشه یا نمودار قابل نمایش نیست.
				</strong>

				<span>
					{state.error?.message}
				</span>
			</div>
		);
	}

	return (
		<div
			className={rootClassName}
			style={rootStyle}
			data-chart-id={
				chart.chart_id
			}
		>
			<Plot
				data={
					plotProperties?.data ||
					[]
				}
				layout={
					plotProperties?.layout ||
					{}
				}
				config={
					plotProperties?.config ||
					{}
				}
				useResizeHandler
				className="plotly-chart__plot"
				style={{
					width: '100%',
					height: '100%'
				}}
				onError={(error) => {
					console.error(
						`Plotly rendering error for "${chart.chart_id}":`,
						error
					);
				}}
			/>
		</div>
	);
}

ChartRenderer.propTypes = {
	chart: PropTypes.shape({
		chart_id:
			PropTypes.oneOfType([
				PropTypes.string,
				PropTypes.number
			]).isRequired,

		figure:
			PropTypes.object,

		traces:
			PropTypes.arrayOf(
				PropTypes.object
			),

		layout:
			PropTypes.object
	}).isRequired,

	height:
		PropTypes.number,

	className:
		PropTypes.string,

	style:
		PropTypes.object,

	config:
		PropTypes.object,

	onError:
		PropTypes.func
};